// src/features/leases/components/FinancialsAtAGlanceCard.jsx
//
// LEASE-DETAIL-V5-S2 — "Financials at a glance" summary pane for the
// Overview tab 2×2 grid (top-right quadrant).
//
// Shows: monthly cost, net rent/yr, contractual rent/yr, total lifetime
// cost, escalation type, escalation rate, plus a "Full financials →" link
// that switches to the Financials tab via the onTabChange callback.
//
// Uses EditableLeaseField (canWrite=false) for every field row so provenance
// badges and "Not specified" muted styling are preserved — identical semantics
// to LeaseSummaryCard and FinancialTermsCard.
//
// Money values are FX-converted to displayCcy via convertAmount, matching
// the pattern in FinancialTermsCard.

import React from "react";
import { T } from "../../../app/tokens.js";
import { PaneHeading } from "../../../components/PaneHeading.jsx";
import { EditableLeaseField } from "./EditableLeaseField.jsx";
import { sourceFor } from "../LeaseSummaryTab.jsx";
import { fmtMoney } from "../../../utils/format.js";
import { convertAmount } from "../../../utils/fxConvert.js";
import { AnnualRentBreakdown, hasRecoveryBasis, netRentDisplay } from "./AnnualRentBreakdown.jsx";
import { leaseAnnualHeadlines } from "../../../shared/leaseAnnualHeadlines.js";
import { MonthlyRentBasis } from "./MonthlyRentBasis.jsx";

const CARD_STYLE = {
  background:    T.card,
  borderRadius:  6,
  padding:       "11px 13px",
  display:       "flex",
  flexDirection: "column",
  flex:          1,
};

export function FinancialsAtAGlanceCard({ computed, lease, displayCcy, rates, onTabChange }) {
  const nativeCcy      = lease?.currency || "USD";
  const effectiveCcy   = displayCcy || nativeCcy;
  const sameCcy        = effectiveCcy === nativeCcy;
  const canConvert     = sameCcy || (rates != null && convertAmount(1, nativeCcy, effectiveCcy, rates) != null);
  const usedCcy        = canConvert ? effectiveCcy : nativeCcy;
  const headlines = leaseAnnualHeadlines(lease, computed);

  function toUsed(amount) {
    if (amount == null) return null;
    return convertAmount(amount, headlines.currency, usedCcy, rates);
  }

  function renderMoney(amount, currency = null) {
    const v = currency == null ? toUsed(amount) : amount;
    if (v == null) return null;
    return (
      <span style={{ fontSize: T.fsBase, fontWeight: T.fwBold, color: T.dark }}>
        {fmtMoney(v, currency ?? usedCcy)}
      </span>
    );
  }

  const year = new Date().getFullYear();
  // Preserve an explicit unknown net value; only older omitted fields use
  // the compatibility alias. Null must not borrow another amount.
  const recoveryBasis = headlines.basis !== "monthly_actual" && hasRecoveryBasis(lease);
  const recordedNet = recoveryBasis ? netRentDisplay(lease, effectiveCcy, rates) : null;
  const billed = recoveryBasis ? lease.annualRentUsd
    : headlines.net;

  const base = {
    locationId: lease?.locationId,
    leaseId:    lease?.id,
    lease,
    canWrite:   false,
  };

  return (
    <section data-testid="financials-at-a-glance-card" style={CARD_STYLE}>
      <PaneHeading style={{ marginBottom: 10 }}>Financials at a glance</PaneHeading>

      <EditableLeaseField
        {...base}
        field="monthlyRent"
        emptyLabel={headlines.monthlyDisplay.basis === "not_in_source" ? "Not in source" : "Unavailable"}
        label="Monthly rent"
        value={headlines.monthly}
        render={() => <>
          {headlines.monthlyDisplay.variable ? "Variable" : renderMoney(convertAmount(headlines.monthly, headlines.monthlyDisplay.currency, usedCcy, rates), usedCcy)}
          <MonthlyRentBasis derivation={headlines.monthlyDisplay} />
        </>}
        source={headlines.monthlyDisplay.basis === "monthly_actual" ? "spacebase" : headlines.monthlyDisplay.approximate ? "computed" : sourceFor(lease, "monthlyRent") ?? "computed"}
      />

      <EditableLeaseField
        {...base}
        field="annualRentBilled"
        emptyLabel="Not in source"
        label={`${recoveryBasis ? "Net recorded" : "Net"} / yr (${year})`}
        value={billed}
        render={() => recoveryBasis
          ? renderMoney(recordedNet.amount, recordedNet.currency)
          : renderMoney(billed)}
        source={headlines.basis === "monthly_actual" ? "spacebase" : recoveryBasis ? "computed" : sourceFor(lease, "annualRentBilled") ?? "computed"}
      />
      {recoveryBasis && <AnnualRentBreakdown lease={lease} />}

      <EditableLeaseField
        {...base}
        field="annualRentRunRate"
        emptyLabel="Not in source"
        label={`Contractual / yr (${year})`}
        value={headlines.contractual}
        render={() => renderMoney(convertAmount(headlines.contractual, headlines.contractualCurrency, usedCcy, rates), usedCcy)}
        source={sourceFor(lease, "annualRentRunRate") ?? "computed"}
      />

      <EditableLeaseField
        {...base}
        field="totalLifetimeCost"
        label="Total lifetime cost"
        value={computed?.totalLifetimeCost}
        render={() => renderMoney(convertAmount(computed?.totalLifetimeCost, nativeCcy, usedCcy, rates), usedCcy)}
        source={sourceFor(lease, "totalLifetimeCost") ?? "computed"}
      />

      <EditableLeaseField
        {...base}
        field="escalationType"
        label="Escalation"
        value={lease?.escalationType}
        render={(v) => {
          const rate = lease?.escalationRate;
          const display = rate != null ? `${v} · ${rate}%` : v;
          return (
            <span style={{ fontSize: T.fsBase, fontWeight: T.fwBold, color: T.dark }}>
              {display}
            </span>
          );
        }}
        source={sourceFor(lease, "escalationType")}
      />

      <div style={{ marginTop: "auto", paddingTop: 10, display: "flex", justifyContent: "flex-end" }}>
        <button
          type="button"
          data-testid="financials-at-a-glance-full-link"
          onClick={() => typeof onTabChange === "function" && onTabChange("financials")}
          style={{
            background:      "none",
            border:          "none",
            padding:         0,
            cursor:          "pointer",
            color:           T.blue,
            fontSize:        T.fsSm,
            fontWeight:      T.fwSemibold,
            textDecoration:  "underline",
          }}
        >
          Full financials →
        </button>
      </div>
    </section>
  );
}
